/** @file Key selection rules: replace, add (Shift) or toggle (Ctrl/Cmd); a moved selection follows its keys. */
import type { KeyRef } from '../../../stores/types';
import type { SelectMode } from '../../../stores/updateSelection';
import { dopeKeys } from './dopeKeys';

/** `refs` without repeated keys, first occurrence kept. */
function unique(refs: readonly KeyRef[]): KeyRef[] {
  const seen = new Set<string>();
  return refs.filter((r) => {
    const id = dopeKeys.idOf(r);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** Pure selection operations over SelectionSlice.keys. */
export const keySelection = {
  /** replace: just `picked`; add: union; toggle: drop `picked` when all are selected, else add the missing ones. */
  apply(current: readonly KeyRef[], picked: readonly KeyRef[], mode: SelectMode): KeyRef[] {
    if (mode === 'replace') return unique(picked);
    const have = new Set(current.map((r) => dopeKeys.idOf(r)));
    const fresh = unique(picked).filter((r) => !have.has(dopeKeys.idOf(r)));
    if (mode === 'add' || fresh.length > 0) return [...current, ...fresh];
    const drop = new Set(picked.map((r) => dopeKeys.idOf(r)));
    return current.filter((r) => !drop.has(dopeKeys.idOf(r)));
  },
  /** True when every one of `refs` is selected (and there is at least one). */
  covers(current: readonly KeyRef[], refs: readonly KeyRef[]): boolean {
    const have = new Set(current.map((r) => dopeKeys.idOf(r)));
    return refs.length > 0 && refs.every((r) => have.has(dopeKeys.idOf(r)));
  },
  /** The selection after its keys moved by `delta` frames. */
  shift(current: readonly KeyRef[], delta: number): KeyRef[] {
    return current.map((r) => ({ ...r, frame: r.frame + delta }));
  },
};
